// import { createSlice } from '@reduxjs/toolkit';

// // 🔹 Define version for future state migrations
// const SLICE_VERSION = '1.0';

// // 🔹 Export initial state for versioned state restoration
// export const initialState = {
//   _version: SLICE_VERSION,
//   defaultTabs: [
//     { id: 'my-invoices', name: 'My Invoices' },
//     { id: 'charges-audit', name: 'Charges Audit' },
//     { id: 'estimated-charges', name: 'Estimated Charges' },
//     { id: 'templates-invoices', name: 'Templates' },
//     { id: 'drafts-invoices', name: 'Drafts' },
//   ],
//   dynamicTabs: [],
//   activeTab: 0,
//   auditRefreshKey: 0,
// };

// // 🔹 Slice definition
// const invoicesSlice = createSlice({
//   name: 'invoices',
//   initialState,
//   reducers: {
//     addTab: (state, action) => {
//       const { id, name, type, data } = action.payload || {};
//       const dynamicId =
//         id ||
//         (window.crypto?.randomUUID?.() ??
//           `${Date.now()}-${Math.random().toString(36).slice(2, 15)}-${state.dynamicTabs.length}`);

//       const tabName = name || `New Invoice ${state.dynamicTabs.length + 1}`;
//       const finalId = dynamicId.toString().startsWith('dyn-') ? dynamicId : `dyn-${dynamicId}`;

//       state.dynamicTabs.push({
//         id: finalId,
//         name: tabName,
//         type: type || 'new',
//         data: data || null,
//         isDynamic: true,
//         createdAt: Date.now(),
//       });

//       state.activeTab = state.defaultTabs.length + state.dynamicTabs.length - 1;
//     },

//     removeTab: (state, action) => {
//       const { tabIndex } = action.payload;
//       if (tabIndex < state.defaultTabs.length) return;

//       const dynamicIndex = tabIndex - state.defaultTabs.length;
//       state.dynamicTabs.splice(dynamicIndex, 1);

//       state.activeTab = Math.min(
//         state.activeTab,
//         state.defaultTabs.length + state.dynamicTabs.length - 1
//       );
//     },

//     setActiveTab: (state, action) => {
//       const { index } = action.payload;
//       const totalTabs = state.defaultTabs.length + state.dynamicTabs.length;

//       if (index >= 0 && index < totalTabs) {
//         state.activeTab = index;
//       }
//     },

//     resetTabs: (state) => {
//       state.dynamicTabs = [];
//       state.activeTab = 0;
//     },
//     incrementAuditRefresh: state => {
//       state.auditRefreshKey += 1;
//     },
//   },
// });

// // 🔹 Export actions and reducer
// export const { addTab, removeTab, setActiveTab, resetTabs,
//   incrementAuditRefresh } = invoicesSlice.actions;
// export default invoicesSlice.reducer;

import { createSlice } from '@reduxjs/toolkit';
import { pushDynamicTab } from '../utils/dynamicTabs';

// 🔹 Define version for future state migrations
const SLICE_VERSION = '1.0';

const getAllTabs = (state) => [...state.defaultTabs, ...state.dynamicTabs];

// Record the tab the user is leaving so a later tab closure can return to it.
const pushTabHistory = (state, tabId) => {
  if (!tabId) return;
  state.tabHistory = state.tabHistory.filter((id) => id !== tabId);
  state.tabHistory.push(tabId);
};

// 🔹 Export initial state for versioned state restoration
export const initialState = {
    _version: SLICE_VERSION,
    defaultTabs: [
        { id: 'my-invoices', name: 'My Invoices' },
        { id: 'charges-audit', name: 'Charges Audit' },
        { id: 'estimated-charges', name: 'Estimated Charges' },
        // { id: 'templates-invoices', name: 'Templates' },
        { id: 'drafts-invoices', name: 'Drafts' },
        { id: 'rejected-invoices',      name: 'Rejected' },
    ],
    dynamicTabs: [],
    activeTab: 0,
    // Stack of previously active tab ids, most-recent-last — lets removeTab send the
    // user back to whichever tab/page they were actually viewing before, not tab 0.
    tabHistory: [],
    auditRefreshKey: 0,
    // ⬇️ keep your local approve/reject overrides here
    chargeAuditOverrides: {},
    // remember pagination/page size per view
    listViewState: {},
};

const invoicesSlice = createSlice({
    name: 'invoices',
    initialState,
    reducers: {
        addTab: (state, action) => {
            const { id, name, type, data, shipments, contentType, baseId, searchQuery } = action.payload || {};
            const dynamicId =
                id ||
                (window.crypto?.randomUUID?.() ??
                    `${Date.now()}-${Math.random().toString(36).slice(2, 15)}-${state.dynamicTabs.length}`);

            const tabName = name || `New Invoice ${state.dynamicTabs.length + 1}`;
            const finalId = dynamicId.toString().startsWith('dyn-') ? dynamicId : `dyn-${dynamicId}`;

            const prevActiveId = getAllTabs(state)[state.activeTab]?.id;
            const added = pushDynamicTab(
                state,
                {
                    id: finalId,
                    name: tabName,
                    type: type || 'new',
                    data: data || null,
                    shipments: shipments || [],
                    contentType: contentType || null,
                    baseId: baseId || null,
                    searchQuery: searchQuery ?? null,

                    isDynamic: true,
                    createdAt: Date.now(),
                },
                state.defaultTabs.length
            );
            if (added) pushTabHistory(state, prevActiveId);

        },

        removeTab: (state, action) => {
            const { tabIndex } = action.payload;
            if (tabIndex < state.defaultTabs.length) return;

            const tabsBefore = getAllTabs(state);
            const removedId = tabsBefore[tabIndex]?.id;
            const activeId = tabsBefore[state.activeTab]?.id;
            const wasActive = tabIndex === state.activeTab;

            const dynamicIndex = tabIndex - state.defaultTabs.length;
            state.dynamicTabs.splice(dynamicIndex, 1);
            state.tabHistory = state.tabHistory.filter((id) => id !== removedId);

            const tabsAfter = getAllTabs(state);

            if (!wasActive) {
                // The tab the user was viewing wasn't the one closed — stay on it, just
                // recompute its index since removing an earlier tab shifts the array.
                const newIndex = tabsAfter.findIndex((t) => t.id === activeId);
                state.activeTab = newIndex >= 0 ? newIndex : Math.min(state.activeTab, tabsAfter.length - 1);
                return;
            }

            // The active tab itself was closed — return to the last tab/page the user
            // was actually on, walking back through history until one still exists.
            while (state.tabHistory.length) {
                const candidateId = state.tabHistory.pop();
                const idx = tabsAfter.findIndex((t) => t.id === candidateId);
                if (idx >= 0) {
                    state.activeTab = idx;
                    return;
                }
            }

            // No usable history — fall back to the default My Invoices tab.
            state.activeTab = 0;
        },

        setActiveTab: (state, action) => {
            const { index } = action.payload;
            const totalTabs = state.defaultTabs.length + state.dynamicTabs.length;
            if (index >= 0 && index < totalTabs && index !== state.activeTab) {
                pushTabHistory(state, getAllTabs(state)[state.activeTab]?.id);
                state.activeTab = index;
            }
        },

        resetTabs: (state) => {
            state.dynamicTabs = [];
            state.activeTab = 0;
            state.tabHistory = [];
        },

        incrementAuditRefresh: (state) => {
            state.auditRefreshKey += 1;
        },

        // ⬇️ New: locally override a single charge's audit status
        setLocalChargeStatus: (state, action) => {
            const { chargeId, status } = action.payload;
            state.chargeAuditOverrides[chargeId] = status;
        },

        setListViewState: (state, action) => {
            const { stateKey, currentPage, itemsPerPage, sortBy, ascending } = action.payload || {};
            if (!stateKey) return;
            state.listViewState = state.listViewState || {};
            state.listViewState[stateKey] = {
                ...(state.listViewState[stateKey] || {}),
                currentPage,
                itemsPerPage,
                sortBy,
                ascending,
            };
        },
    },
});

export const {
    addTab,
    removeTab,
    setActiveTab,
    resetTabs,
    incrementAuditRefresh,
    setLocalChargeStatus,
    setListViewState,
} = invoicesSlice.actions;

export default invoicesSlice.reducer;
